import { supabase } from "./supabase";
import { extrairTexto, ErroDePdf, EXPLICACAO } from "./pdfTexto";
import { MAXIMO_DE_ARQUIVOS, tituloDoEnvio, type TipoPericia } from "./processos";

// O IO do Leitor Inteligente de Processos (000171).
//
// Este módulo transporta. Quem decide se o aluno pode usar é o banco
// (`conteudo_liberado('leitor-de-processos')` dentro de `lp_criar_analise`, que
// pergunta a `abrir_card`); quem decide o que aparece é
// `LeitorDeProcessos.tsx`. Aqui não há regra de acesso nem de apresentação.
//
// ---------------------------------------------------------------------------
// O texto do processo SOBE UMA VEZ, e não volta
// ---------------------------------------------------------------------------
// `lp_criar_analise` recebe o texto extraído; `lp_abrir_analise` NÃO o devolve.
// O texto tem até 2 milhões de caracteres e a tela não o desenha — quem o lê são
// as Edge Functions, pela chave de serviço. Trafegar megabytes que ninguém
// mostra é o que a `sec_meus_emitidos` da 000136 existe para lembrar: leitura de
// tela pede função com o recorte da tela.
//
// ---------------------------------------------------------------------------
// O checklist é gravado item por item, e o erro de ESCRITA não apaga a tela
// ---------------------------------------------------------------------------
// A cicatriz do calendário: `setErro(true)` numa marcação que falhou trocava o
// mês inteiro pela mensagem "não conseguimos carregar". Sem leitura não há tela;
// sem UMA escrita, há tela e falta um tique. Por isso `marcarItem` devolve
// `boolean` e quem chama decide — nunca derruba a análise.

export type SituacaoDaAnalise = "em_analise" | "ok" | "falha";

export type ResumoDeAnalise = {
  id: string;
  titulo: string;
  tipo: TipoPericia;
  situacao: SituacaoDaAnalise;
  erro: string | null;
  paginas: number;
  /** Quantas a IA leu. Nulo enquanto espera; menor que `paginas` quando truncou. */
  paginas_lidas: number | null;
  criado_em: string;
  arquivos: number;
  itens: number;
  feitos: number;
};

export type ArquivoDaAnalise = {
  nome: string;
  paginas: number;
  caracteres: number;
};

export type ItemDoChecklist = {
  id: string;
  texto: string;
  pagina: string | null;
  fonte: string | null;
  feito: boolean;
};

export type Fala = { papel: "user" | "assistant"; conteudo: string };

/** O que a IA devolveu, menos o checklist — a forma é a do prompt da origem. */
export type ResultadoDaIA = {
  relatorio?: {
    identificacao?: string;
    objetoAcao?: string;
    objetoPericia?: string;
    documentosAnalisados?: { nome?: string; fase?: string; classe?: string; paginas?: string }[];
    pontosControvertidos?: (string | { texto?: string; fonte?: string })[];
    quesitos?: { texto?: string; pagina?: string; fonte?: string }[];
    documentosRelevantes?: { nome?: string; pagina?: string; fonte?: string }[];
  };
  linhaDoTempo?: { data?: string; evento?: string; descricao?: string; pagina?: string; fonte?: string }[];
  alertas?: { tipo?: string; descricao?: string; pagina?: string; fonte?: string }[];
};

export type AnaliseAberta = {
  analise: {
    id: string;
    titulo: string;
    tipo: TipoPericia;
    situacao: SituacaoDaAnalise;
    erro: string | null;
    modelo: string | null;
    paginas: number;
    caracteres: number;
    caracteres_enviados: number | null;
    /** Quantas páginas a IA leu — CONTADAS, não estimadas (000173). */
    paginas_lidas: number | null;
    criado_em: string;
    resultado: ResultadoDaIA | null;
  };
  arquivos: ArquivoDaAnalise[];
  itens: ItemDoChecklist[];
  conversa: Fala[];
};

/** O arquivo já lido, esperando o envio. */
export type ArquivoLido = {
  nome: string;
  texto: string;
  paginas: number;
  caracteres: number;
};

export async function minhasAnalises(): Promise<ResumoDeAnalise[]> {
  const { data, error } = await supabase.rpc("lp_minhas_analises");
  if (error) throw error;
  return (data ?? []) as ResumoDeAnalise[];
}

export async function abrirAnalise(id: string): Promise<AnaliseAberta | null> {
  const { data, error } = await supabase.rpc("lp_abrir_analise", { p_analise: id });
  if (error) throw error;
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) return null;
  return r as unknown as AnaliseAberta;
}

/**
 * Lê um PDF e devolve o texto. A falha volta NOMEADA — nunca como "não deu
 * certo", porque as quatro causas pedem quatro ações diferentes do perito.
 */
export async function lerPdf(
  arquivo: File,
  aoProgredir?: (pagina: number, de: number) => void,
): Promise<{ ok: true; lido: ArquivoLido } | { ok: false; mensagem: string }> {
  try {
    const e = await extrairTexto(arquivo, aoProgredir);
    return {
      ok: true,
      lido: { nome: arquivo.name, texto: e.texto, paginas: e.paginas, caracteres: e.caracteres },
    };
  } catch (erro) {
    if (erro instanceof ErroDePdf) return { ok: false, mensagem: erro.message };
    return { ok: false, mensagem: EXPLICACAO.falha };
  }
}

/**
 * Junta os documentos como o servidor espera: um vai puro, vários vão com o
 * separador `=== DOCUMENTO N: nome ===` antes de cada um.
 *
 * O formato é o mesmo de `juntarDocumentos` em
 * `supabase/functions/_shared/leitor-de-processos.ts`, e tem de continuar sendo:
 * é por esse separador que `cortar()` divide a cota, e é ele que o prompt cita
 * para exigir o campo `fonte` de cada item. Divergir aqui faria o servidor tratar
 * vários documentos como um só — e a cota deixaria de existir sem nenhum sinal.
 */
export function juntarParaEnvio(lidos: ArquivoLido[]): string {
  if (lidos.length === 1) return lidos[0].texto;
  return lidos
    .map((d, i) => `=== DOCUMENTO ${i + 1}: ${d.nome} ===\n${d.texto}`)
    .join("\n\n");
}

/** O teto do texto que o banco aceita (`char_length(texto) between 1 and 2000000`). */
export const TETO_DO_BANCO = 2000000;

export type Criacao =
  | { ok: true; id: string }
  | { ok: false; motivo: string; mensagem: string };

const MENSAGEM_DO_MOTIVO: Record<string, string> = {
  sem_sessao: "A sua sessão expirou. Entre de novo para continuar.",
  sem_acesso:
    "Este curso não está liberado para você agora. Se você acabou de comprar, " +
    "pode levar alguns minutos; se há contrato pendente, ele aparece na página do curso.",
  sem_texto:
    "Não consegui ler texto nenhum nos PDFs enviados. Processo digitalizado como " +
    "imagem não tem texto para extrair.",
  quantidade: `Envie de 1 a ${MAXIMO_DE_ARQUIVOS} arquivos por análise.`,
  arquivos_invalidos: "Não entendi a lista de arquivos. Recarregue a página e tente de novo.",
};

/**
 * Cria a análise e dispara o modelo.
 *
 * Duas etapas, e nesta ordem: primeiro o banco grava (o texto passa a existir e
 * ter dono), depois a Edge Function é chamada com o ID. Se a segunda falhar, a
 * análise fica em `em_analise` e a tela oferece "analisar de novo" — nada se
 * perde, e o texto não precisa ser reenviado.
 */
export async function criarAnalise(
  lidos: ArquivoLido[],
  tipo: TipoPericia,
): Promise<Criacao> {
  if (lidos.length < 1 || lidos.length > MAXIMO_DE_ARQUIVOS) {
    return { ok: false, motivo: "quantidade", mensagem: MENSAGEM_DO_MOTIVO.quantidade };
  }

  const texto = juntarParaEnvio(lidos);
  if (texto.length > TETO_DO_BANCO) {
    // Truncar em silêncio aqui seria o defeito da origem por outro caminho.
    // Quem chama já sabe do teto pelo aviso da tela; esta é a última defesa.
    return {
      ok: false,
      motivo: "grande_demais",
      mensagem:
        "Os arquivos somam mais texto do que uma análise aceita. Divida o processo " +
        "em partes e analise a mais relevante primeiro.",
    };
  }

  const { data, error } = await supabase.rpc("lp_criar_analise", {
    p_tipo: tipo,
    p_titulo: tituloDoEnvio(lidos.map((l) => l.nome)),
    p_texto: texto,
    p_paginas: lidos.reduce((s, l) => s + l.paginas, 0),
    p_arquivos: lidos.map((l) => ({
      nome: l.nome,
      paginas: l.paginas,
      caracteres: l.caracteres,
    })),
  });

  if (error) {
    return { ok: false, motivo: "erro", mensagem: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) {
    const motivo = String(r.motivo ?? "erro");
    return {
      ok: false,
      motivo,
      mensagem: String(r.mensagem ?? MENSAGEM_DO_MOTIVO[motivo] ?? "Não deu para criar a análise."),
    };
  }

  return { ok: true, id: String(r.id) };
}

export type Disparo = { ok: boolean; situacao?: string; erro?: string | null; mensagem?: string };

/**
 * Chama a Edge Function que analisa. Devolve o que aconteceu — quem desenha a
 * espera é a tela.
 */
export async function analisar(id: string): Promise<Disparo> {
  const { data, error } = await supabase.functions.invoke("analisar-processo", {
    body: { analise_id: id },
  });

  if (error) {
    // 503 (sem chave) e 403 vêm por aqui. A mensagem do corpo é a útil; a do
    // `error` é genérica ("non-2xx status code"), e mostrá-la ao aluno não
    // informa nada.
    const corpo = (data ?? {}) as Record<string, unknown>;
    return {
      ok: false,
      mensagem: String(
        corpo.erro ??
          "A análise não pôde ser feita agora. O processo continua salvo — tente de novo em instantes.",
      ),
    };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  return {
    ok: r.ok === true,
    situacao: r.situacao ? String(r.situacao) : undefined,
    erro: r.erro ? String(r.erro) : null,
    mensagem: r.ok === true ? undefined : String(r.motivo ?? r.erro ?? "A análise falhou."),
  };
}

/** Marca ou desmarca um item. Devolve se gravou — a tela NÃO se derruba por isso. */
export async function marcarItem(itemId: string, feito: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc("lp_marcar_item", {
    p_item: itemId,
    p_feito: feito,
  });
  if (error) return false;
  return ((data ?? {}) as Record<string, unknown>).ok === true;
}

export async function apagarAnalise(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("lp_apagar_analise", { p_analise: id });
  if (error) return false;
  return ((data ?? {}) as Record<string, unknown>).ok === true;
}

/**
 * Pergunta ao assistente sobre o processo, lendo a resposta em pedaços.
 *
 * O corpo leva só `{analise_id, pergunta}`: o contexto (o texto do processo, o
 * resultado da análise e o histórico) é montado no SERVIDOR. Na origem o
 * navegador mandava os 60.000 caracteres do processo e o array inteiro de
 * mensagens em cada pergunta — o que significa que qualquer um com sessão podia
 * mandar o texto que quisesse e reescrever o que a IA "disse" antes.
 */
export async function perguntar(
  id: string,
  pergunta: string,
  aoChegar: (pedaco: string) => void,
): Promise<{ ok: true } | { ok: false; mensagem: string }> {
  const { data: sessao } = await supabase.auth.getSession();
  const token = sessao.session?.access_token;
  if (!token) return { ok: false, mensagem: "A sua sessão expirou. Entre de novo." };

  const base = import.meta.env.VITE_SUPABASE_URL as string;
  let resposta: Response;
  try {
    resposta = await fetch(`${base}/functions/v1/conversar-processo`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({ analise_id: id, pergunta }),
    });
  } catch {
    return { ok: false, mensagem: "Não deu para falar com o assistente. Confira a conexão." };
  }

  if (!resposta.ok || !resposta.body) {
    let mensagem = "O assistente não respondeu agora. Tente de novo.";
    try {
      const corpo = await resposta.json();
      if (corpo?.erro) mensagem = String(corpo.erro);
    } catch { /* corpo não-JSON: fica a mensagem genérica */ }
    return { ok: false, mensagem };
  }

  const leitor = resposta.body.getReader();
  const dec = new TextDecoder();
  // O corpo é TEXTO PURO, em pedaços — não SSE. É o desenho de `perguntar-ia`:
  // a origem monta `data:` de um lado e desmonta do outro, com buffer de linha
  // parcial no cliente, e todo esse código só existe por causa do formato.
  while (true) {
    const { done, value } = await leitor.read();
    if (done) break;
    const pedaco = dec.decode(value, { stream: true });
    if (pedaco) aoChegar(pedaco);
  }

  return { ok: true };
}
